import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  Clock,
  MessageSquarePlus,
  ClipboardList,
  ListTodo,
  GripVertical,
  MapPin,
  Plus,
  DollarSign,
  User,
  Tag,
  Pencil,
  Trash2,
  Phone,
  Mail,
  Video,
  Users,
  Coffee,
  FileText,
  StickyNote
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToastNotification } from '@/contexts/ToastContext';

// Utility function to calculate relative time
const getRelativeTime = (timestamp) => {
  if (!timestamp) return 'Never';
  
  const now = new Date();
  const date = new Date(timestamp);
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60);
    return `${mins}m ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h ago`;
  }
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d ago`;
  }
  if (diffInSeconds < 2592000) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return `${weeks}w ago`;
  }
  const months = Math.floor(diffInSeconds / 2592000);
  return `${months}mo ago`;
};

// Pipeline stages configuration for Hard Circle
const PIPELINE_STAGES = [
  { 
    id: 'qualified', 
    label: 'Qualified', 
    description: 'Contact has been vetted and approved internally. Interest is serious and aligned with current fund or deal. Owner and internal context are clearly assigned. No legal or financial commitment yet.' 
  },
  { 
    id: 'committed-verbal', 
    label: 'Committed (Verbal)', 
    description: 'Clear verbal or written (non-binding) commitment expressed. Expected amount and rough timing are known. Internal team considers this capital "probable". Still reversible, but confidence is high.' 
  },
  { 
    id: 'legal-in-progress', 
    label: 'Legal in Progress', 
    description: 'Legal documents shared, reviewed, or in negotiation. Compliance checks underway or completed. Amount and entity details are confirmed. Delays or blockers should be visible here.' 
  },
  { 
    id: 'executing', 
    label: 'Executing', 
    description: 'Final signatures completed or in final steps. Wiring or on-chain transfer is imminent. Funds are operationally expected, not speculative. Requires close coordination with ops/compliance.' 
  },
  { 
    id: 'wired-settled', 
    label: 'Wired / Settled', 
    description: 'Capital successfully received and confirmed. Transaction fully settled. Contact exits the active pipeline. Becomes historical reference and analytics input.' 
  },
];

// Helper to create timestamps relative to now
const createTimestamp = (hoursAgo) => new Date(Date.now() - hoursAgo * 60 * 60 * 1000).toISOString();

// Sample contact data for Hard Circle
const initialContacts = [
  {
    id: '1',
    name: 'Jonathan Blake',
    organization: 'Meridian Wealth Partners',
    estimatedAmount: '$15M',
    region: 'NA',
    lastTouchTimestamp: createTimestamp(24), // 1 day ago
    owner: 'OL',
    stage: 'qualified',
    tags: ['Institutional'],
    notes: [],
    interactions: []
  },
  {
    id: '2',
    name: 'Victoria Sterling',
    organization: 'Crown Capital Group',
    estimatedAmount: '$25M',
    region: 'EMEA',
    lastTouchTimestamp: createTimestamp(48), // 2 days ago
    owner: 'JK',
    stage: 'committed-verbal',
    tags: ['Family Office'],
    notes: [],
    interactions: []
  },
  {
    id: '3',
    name: 'Marcus Chen',
    organization: 'Pacific Sovereign Fund',
    estimatedAmount: '$50M',
    region: 'APAC',
    lastTouchTimestamp: createTimestamp(24), // 1 day ago
    owner: 'MR',
    stage: 'legal-in-progress',
    tags: ['Sovereign'],
    notes: [],
    interactions: []
  },
  {
    id: '4',
    name: 'Alexandra Hoffman',
    organization: 'Deutsche Invest AG',
    estimatedAmount: '$30M',
    region: 'EMEA',
    lastTouchTimestamp: createTimestamp(3), // 3 hours ago
    owner: 'OL',
    stage: 'executing',
    tags: ['Institutional'],
    notes: [],
    interactions: []
  },
  {
    id: '5',
    name: 'Richard Thornton',
    organization: 'Pinnacle Trust',
    estimatedAmount: '$20M',
    region: 'NA',
    lastTouchTimestamp: createTimestamp(120), // 5 days ago
    owner: 'JK',
    stage: 'wired-settled',
    tags: ['HNW'],
    notes: [],
    interactions: []
  },
  {
    id: '6',
    name: 'Sophia Nakamura',
    organization: 'Tokyo Capital Partners',
    estimatedAmount: '$12M',
    region: 'APAC',
    lastTouchTimestamp: createTimestamp(48), // 2 days ago
    owner: 'OL',
    stage: 'qualified',
    tags: ['Pension'],
    notes: [],
    interactions: []
  },
  {
    id: '7',
    name: 'William Crawford',
    organization: 'Atlantic Ventures',
    estimatedAmount: '$8M',
    region: 'NA',
    lastTouchTimestamp: createTimestamp(96), // 4 days ago
    owner: 'MR',
    stage: 'committed-verbal',
    tags: ['VC'],
    notes: [],
    interactions: []
  },
  {
    id: '8',
    name: 'Elena Petrova',
    organization: 'Northern Star Holdings',
    estimatedAmount: '$18M',
    region: 'EMEA',
    lastTouchTimestamp: createTimestamp(24), // 1 day ago
    owner: 'JK',
    stage: 'legal-in-progress',
    tags: ['Family Office'],
    notes: [],
    interactions: []
  },
  {
    id: '9',
    name: 'David Morrison',
    organization: 'Granite Capital',
    estimatedAmount: '$40M',
    region: 'NA',
    lastTouchTimestamp: createTimestamp(6), // 6 hours ago
    owner: 'OL',
    stage: 'executing',
    tags: ['Endowment'],
    notes: [],
    interactions: []
  },
  {
    id: '10',
    name: 'Catherine Wu',
    organization: 'Jade River Investments',
    estimatedAmount: '$22M',
    region: 'APAC',
    lastTouchTimestamp: createTimestamp(72), // 3 days ago
    owner: 'MR',
    stage: 'wired-settled',
    tags: ['Institutional'],
    notes: [],
    interactions: []
  },
  {
    id: '11',
    name: 'Henrik Larsson',
    organization: 'Nordic Growth Fund',
    estimatedAmount: '$35M',
    region: 'EMEA',
    lastTouchTimestamp: createTimestamp(24), // 1 day ago
    owner: 'JK',
    stage: 'qualified',
    tags: ['Pension'],
    notes: [],
    interactions: []
  },
  {
    id: '12',
    name: 'Patricia Andrade',
    organization: 'Banco Futuro SA',
    estimatedAmount: '$10M',
    region: 'LATAM',
    lastTouchTimestamp: createTimestamp(48), // 2 days ago
    owner: 'OL',
    stage: 'committed-verbal',
    tags: ['Bank'],
    notes: [],
    interactions: []
  },
];

// Contact Card Component
const ContactCard = ({ contact, onDragStart, onDragEnd, isDragging, onEditContact, onDeleteContact, onAddNote, onLogInteraction }) => {
  const [showActions, setShowActions] = useState(false);
  const [isDraggingLocal, setIsDraggingLocal] = useState(false);
  const [, forceUpdate] = useState(0);

  // Auto-refresh relative time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate(n => n + 1);
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const handleDragStart = (e) => {
    setIsDraggingLocal(true);
    // Set drag image
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', contact.id);
    // Call parent handler
    onDragStart(e, contact);
  };

  const handleDragEnd = (e) => {
    setIsDraggingLocal(false);
    onDragEnd(e);
  };

  // Count notes and interactions for badges
  const noteCount = contact.notes?.length || 0;
  const interactionCount = contact.interactions?.length || 0;
  
  // Calculate relative time from timestamp
  const lastTouchDisplay = getRelativeTime(contact.lastTouchTimestamp);

  return (
    <div
      draggable="true"
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      className={`group relative bg-[#111318] border border-white/6 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-white/12 transition-all duration-150 ${
        isDraggingLocal ? 'opacity-50 scale-95' : 'opacity-100'
      }`}
    >
      {/* Drag Handle */}
      <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-40 transition-opacity pointer-events-none">
        <GripVertical size={14} className="text-zinc-500" />
      </div>

      {/* Card Content */}
      <div className="pl-3">
        {/* Header: Name + Owner */}
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-zinc-100 truncate">
              {contact.name}
            </h4>
            <p className="text-xs text-zinc-500 truncate flex items-center gap-1">
              <Building2 size={10} className="flex-shrink-0" />
              {contact.organization}
            </p>
          </div>
          <div className="w-6 h-6 rounded-full bg-[#1a1d24] border border-white/10 flex items-center justify-center text-[10px] font-medium text-zinc-400 flex-shrink-0">
            {contact.owner}
          </div>
        </div>

        {/* Middle Row: Amount + Region */}
        <div className="flex items-center gap-3 mb-2">
          {contact.estimatedAmount && (
            <span className="text-xs font-medium text-emerald-400/90">
              {contact.estimatedAmount}
            </span>
          )}
          <span className="text-[10px] text-zinc-500 flex items-center gap-0.5">
            <MapPin size={9} />
            {contact.region}
          </span>
        </div>

        {/* Footer: Last Touch + Tag + Badges */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-zinc-600 flex items-center gap-1">
              <Clock size={10} />
              {lastTouchDisplay}
            </span>
            {/* Note and Interaction badges */}
            {noteCount > 0 && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-400 flex items-center gap-0.5">
                <StickyNote size={8} />
                {noteCount}
              </span>
            )}
            {interactionCount > 0 && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-400 flex items-center gap-0.5">
                <ClipboardList size={8} />
                {interactionCount}
              </span>
            )}
          </div>
          {contact.tags[0] && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800/50 text-zinc-500 border border-white/5">
              {contact.tags[0]}
            </span>
          )}
        </div>
      </div>

      {/* Quick Actions Overlay */}
      <AnimatePresence>
        {showActions && !isDraggingLocal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="absolute inset-0 bg-[#111318]/95 rounded-lg flex items-center justify-center gap-2 backdrop-blur-sm"
          >
            <button
              className="p-2 rounded-md bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-zinc-200 transition-colors"
              title="Add Note"
              onClick={(e) => { e.stopPropagation(); onAddNote(contact); }}
            >
              <MessageSquarePlus size={14} />
            </button>
            <button
              className="p-2 rounded-md bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-zinc-200 transition-colors"
              title="Log Interaction"
              onClick={(e) => { e.stopPropagation(); onLogInteraction(contact); }}
            >
              <ClipboardList size={14} />
            </button>
            <button
              className="p-2 rounded-md bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-zinc-200 transition-colors"
              title="Create Task"
              onClick={(e) => { e.stopPropagation(); }}
            >
              <ListTodo size={14} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Pipeline Column Component
const PipelineColumn = ({ stage, contacts, onDrop, onDragOver, onDragStart, onDragEnd, dragOverColumn, onEditContact, onDeleteContact, onAddNote, onLogInteraction }) => {
  const isDropTarget = dragOverColumn === stage.id;
  const count = contacts.length;

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragOver(e, stage.id);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDrop(e, stage.id);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      className={`flex flex-col flex-1 min-w-[200px] transition-all duration-150 ${
        isDropTarget ? 'bg-blue-500/10 ring-2 ring-blue-500/30 ring-inset' : ''
      }`}
      style={{ height: '100%' }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
    >
      {/* Column Header */}
      <div className="flex-shrink-0 px-3 py-3 bg-[#0a0a0a] border-b border-white/10">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-100 uppercase tracking-wide">{stage.label}</h3>
          <span className="text-xs text-zinc-400 bg-zinc-800 px-2.5 py-1 rounded-full font-medium">
            {count}
          </span>
        </div>
      </div>

      {/* Column Content - Single unified area for cards and drop zone */}
      <div 
        className="flex-1 p-2 space-y-2 overflow-y-auto"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnter={handleDragEnter}
      >
        {contacts.map((contact) => (
          <ContactCard
            key={contact.id}
            contact={contact}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onEditContact={onEditContact}
            onDeleteContact={onDeleteContact}
            onAddNote={onAddNote}
            onLogInteraction={onLogInteraction}
          />
        ))}
        
        {/* Empty state message when no cards */}
        {contacts.length === 0 && (
          <div className="flex items-center justify-center h-32">
            <p className="text-xs text-zinc-600 leading-relaxed text-center px-3">
              {stage.description}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Create Contact Modal Component
const CreateContactModal = ({ open, onOpenChange, onCreateContact, stages }) => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    estimatedAmount: '',
    region: 'NA',
    owner: 'OL',
    stage: 'qualified',
    tag: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.organization.trim()) {
      return;
    }

    const newContact = {
      id: Date.now().toString(),
      name: formData.name.trim(),
      organization: formData.organization.trim(),
      estimatedAmount: formData.estimatedAmount ? `$${formData.estimatedAmount}` : null,
      region: formData.region,
      lastTouchTimestamp: new Date().toISOString(),
      owner: formData.owner,
      stage: formData.stage,
      tags: formData.tag ? [formData.tag] : [],
      notes: [],
      interactions: []
    };

    onCreateContact(newContact);
    
    // Reset form
    setFormData({
      name: '',
      organization: '',
      estimatedAmount: '',
      region: 'NA',
      owner: 'OL',
      stage: 'qualified',
      tag: ''
    });
    onOpenChange(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add New Contact</DialogTitle>
          <DialogDescription>
            Create a new contact and place them in a pipeline stage.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-zinc-300 text-xs">
              Contact Name <span className="text-red-400">*</span>
            </Label>
            <div className="relative">
              <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., John Smith"
                className="pl-9 bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                required
              />
            </div>
          </div>

          {/* Organization */}
          <div className="space-y-2">
            <Label htmlFor="organization" className="text-zinc-300 text-xs">
              Organization <span className="text-red-400">*</span>
            </Label>
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                id="organization"
                value={formData.organization}
                onChange={(e) => handleChange('organization', e.target.value)}
                placeholder="e.g., Acme Capital Partners"
                className="pl-9 bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                required
              />
            </div>
          </div>

          {/* Estimated Amount & Region - Side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="amount" className="text-zinc-300 text-xs">
                Estimated Amount
              </Label>
              <div className="relative">
                <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  id="amount"
                  value={formData.estimatedAmount}
                  onChange={(e) => handleChange('estimatedAmount', e.target.value.replace(/[^0-9.MBKmbk]/g, ''))}
                  placeholder="e.g., 10M"
                  className="pl-9 bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs">Region</Label>
              <Select value={formData.region} onValueChange={(val) => handleChange('region', val)}>
                <SelectTrigger className="bg-[#111318] border-white/10 text-zinc-300 text-sm focus:border-blue-500/50 focus:ring-blue-500/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1115] border-white/10">
                  <SelectItem value="NA" className="text-sm">NA (North America)</SelectItem>
                  <SelectItem value="EMEA" className="text-sm">EMEA</SelectItem>
                  <SelectItem value="APAC" className="text-sm">APAC</SelectItem>
                  <SelectItem value="LATAM" className="text-sm">LATAM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Owner & Tag - Side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs">Owner</Label>
              <Select value={formData.owner} onValueChange={(val) => handleChange('owner', val)}>
                <SelectTrigger className="bg-[#111318] border-white/10 text-zinc-300 text-sm focus:border-blue-500/50 focus:ring-blue-500/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1115] border-white/10">
                  <SelectItem value="OL" className="text-sm">OL</SelectItem>
                  <SelectItem value="JK" className="text-sm">JK</SelectItem>
                  <SelectItem value="MR" className="text-sm">MR</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tag" className="text-zinc-300 text-xs">Tag</Label>
              <div className="relative">
                <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  id="tag"
                  value={formData.tag}
                  onChange={(e) => handleChange('tag', e.target.value)}
                  placeholder="e.g., Institutional"
                  className="pl-9 bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                />
              </div>
            </div>
          </div>

          {/* Stage Selection */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-xs">Pipeline Stage</Label>
            <Select value={formData.stage} onValueChange={(val) => handleChange('stage', val)}>
              <SelectTrigger className="bg-[#111318] border-white/10 text-zinc-300 text-sm focus:border-blue-500/50 focus:ring-blue-500/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f1115] border-white/10">
                {stages.map(stage => (
                  <SelectItem key={stage.id} value={stage.id} className="text-sm">
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="mt-6 gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-zinc-400 hover:text-zinc-200 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus size={16} />
              Add Contact
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Edit Contact Modal Component
const EditContactModal = ({ open, onOpenChange, onUpdateContact, contact, stages }) => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    estimatedAmount: '',
    region: 'NA',
    owner: 'OL',
    stage: 'qualified',
    tag: ''
  });

  // Update form when contact changes
  React.useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        organization: contact.organization || '',
        estimatedAmount: contact.estimatedAmount ? contact.estimatedAmount.replace('$', '') : '',
        region: contact.region || 'NA',
        owner: contact.owner || 'OL',
        stage: contact.stage || 'qualified',
        tag: contact.tags?.[0] || ''
      });
    }
  }, [contact]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.organization.trim()) {
      return;
    }

    const updatedContact = {
      ...contact,
      name: formData.name.trim(),
      organization: formData.organization.trim(),
      estimatedAmount: formData.estimatedAmount ? `$${formData.estimatedAmount}` : null,
      region: formData.region,
      lastTouchTimestamp: new Date().toISOString(), // Update timestamp on edit
      owner: formData.owner,
      stage: formData.stage,
      tags: formData.tag ? [formData.tag] : []
    };

    onUpdateContact(updatedContact);
    onOpenChange(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!contact) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Contact</DialogTitle>
          <DialogDescription>
            Update contact details and pipeline stage.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="edit-name" className="text-zinc-300 text-xs">
              Contact Name <span className="text-red-400">*</span>
            </Label>
            <div className="relative">
              <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., John Smith"
                className="pl-9 bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                required
              />
            </div>
          </div>

          {/* Organization */}
          <div className="space-y-2">
            <Label htmlFor="edit-organization" className="text-zinc-300 text-xs">
              Organization <span className="text-red-400">*</span>
            </Label>
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                id="edit-organization"
                value={formData.organization}
                onChange={(e) => handleChange('organization', e.target.value)}
                placeholder="e.g., Acme Capital Partners"
                className="pl-9 bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                required
              />
            </div>
          </div>

          {/* Estimated Amount & Region - Side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="edit-amount" className="text-zinc-300 text-xs">
                Estimated Amount
              </Label>
              <div className="relative">
                <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  id="edit-amount"
                  value={formData.estimatedAmount}
                  onChange={(e) => handleChange('estimatedAmount', e.target.value.replace(/[^0-9.MBKmbk]/g, ''))}
                  placeholder="e.g., 10M"
                  className="pl-9 bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs">Region</Label>
              <Select value={formData.region} onValueChange={(val) => handleChange('region', val)}>
                <SelectTrigger className="bg-[#111318] border-white/10 text-zinc-300 text-sm focus:border-blue-500/50 focus:ring-blue-500/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1115] border-white/10">
                  <SelectItem value="NA" className="text-sm">NA (North America)</SelectItem>
                  <SelectItem value="EMEA" className="text-sm">EMEA</SelectItem>
                  <SelectItem value="APAC" className="text-sm">APAC</SelectItem>
                  <SelectItem value="LATAM" className="text-sm">LATAM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Owner & Tag - Side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs">Owner</Label>
              <Select value={formData.owner} onValueChange={(val) => handleChange('owner', val)}>
                <SelectTrigger className="bg-[#111318] border-white/10 text-zinc-300 text-sm focus:border-blue-500/50 focus:ring-blue-500/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1115] border-white/10">
                  <SelectItem value="OL" className="text-sm">OL</SelectItem>
                  <SelectItem value="JK" className="text-sm">JK</SelectItem>
                  <SelectItem value="MR" className="text-sm">MR</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-tag" className="text-zinc-300 text-xs">Tag</Label>
              <div className="relative">
                <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  id="edit-tag"
                  value={formData.tag}
                  onChange={(e) => handleChange('tag', e.target.value)}
                  placeholder="e.g., Institutional"
                  className="pl-9 bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                />
              </div>
            </div>
          </div>

          {/* Last Touch - Display only (auto-updates on save) */}
          {contact?.lastTouchTimestamp && (
            <div className="flex items-center gap-2 text-xs text-zinc-500 bg-zinc-900/30 rounded px-3 py-2">
              <Clock size={12} />
              <span>Last touched: {getRelativeTime(contact.lastTouchTimestamp)}</span>
              <span className="text-zinc-600">• Will update on save</span>
            </div>
          )}

          {/* Stage Selection */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-xs">Pipeline Stage</Label>
            <Select value={formData.stage} onValueChange={(val) => handleChange('stage', val)}>
              <SelectTrigger className="bg-[#111318] border-white/10 text-zinc-300 text-sm focus:border-blue-500/50 focus:ring-blue-500/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f1115] border-white/10">
                {stages.map(stage => (
                  <SelectItem key={stage.id} value={stage.id} className="text-sm">
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="mt-6 gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-zinc-400 hover:text-zinc-200 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Pencil size={16} />
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Add Note Modal Component
const AddNoteModal = ({ open, onOpenChange, onAddNote, onDeleteNote, contact }) => {
  const [noteText, setNoteText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!noteText.trim()) {
      return;
    }

    const newNote = {
      id: Date.now().toString(),
      text: noteText.trim(),
      createdAt: new Date().toISOString(),
      author: 'OL' // Default to current user
    };

    onAddNote(contact.id, newNote);
    setNoteText('');
    onOpenChange(false);
  };

  if (!contact) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote size={18} className="text-amber-400" />
            Notes
          </DialogTitle>
          <DialogDescription>
            Manage notes for <span className="text-zinc-200 font-medium">{contact.name}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Existing notes with delete */}
          {contact.notes && contact.notes.length > 0 && (
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Existing Notes ({contact.notes.length})</Label>
              <div className="max-h-[150px] overflow-y-auto space-y-2">
                {contact.notes.map((note) => (
                  <div key={note.id} className="text-xs bg-zinc-900/50 rounded p-2 border border-white/5 group">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-zinc-400 flex-1">{note.text}</p>
                      <button
                        type="button"
                        onClick={() => onDeleteNote(contact.id, note.id)}
                        className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-all"
                        title="Delete note"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                    <span className="text-[10px] text-zinc-600 mt-1 block">{note.author} · {new Date(note.createdAt).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add new note form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="note-text" className="text-zinc-300 text-xs">
                Add New Note
              </Label>
              <Textarea
                id="note-text"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Write your note here..."
                className="min-h-[100px] bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500/50 focus:ring-amber-500/20 resize-none"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="text-zinc-400 hover:text-zinc-200 hover:bg-white/5"
              >
                Close
              </Button>
              <Button
                type="submit"
                disabled={!noteText.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
              >
                <Plus size={16} />
                Add Note
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Interaction types
const INTERACTION_TYPES = [
  { id: 'call', label: 'Phone Call', icon: Phone },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'meeting', label: 'Meeting', icon: Users },
  { id: 'video', label: 'Video Call', icon: Video },
  { id: 'coffee', label: 'Coffee/Lunch', icon: Coffee },
  { id: 'document', label: 'Document Sent', icon: FileText },
];

// Log Interaction Modal Component
const LogInteractionModal = ({ open, onOpenChange, onLogInteraction, onDeleteInteraction, contact }) => {
  const [formData, setFormData] = useState({
    type: 'call',
    summary: '',
    outcome: '',
    nextStep: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.summary.trim()) {
      return;
    }

    const newInteraction = {
      id: Date.now().toString(),
      type: formData.type,
      summary: formData.summary.trim(),
      outcome: formData.outcome.trim(),
      nextStep: formData.nextStep.trim(),
      createdAt: new Date().toISOString(),
      author: 'OL'
    };

    onLogInteraction(contact.id, newInteraction);
    setFormData({
      type: 'call',
      summary: '',
      outcome: '',
      nextStep: ''
    });
    onOpenChange(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!contact) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList size={18} className="text-purple-400" />
            Interactions
          </DialogTitle>
          <DialogDescription>
            Manage interactions with <span className="text-zinc-200 font-medium">{contact.name}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Existing interactions with delete */}
          {contact.interactions && contact.interactions.length > 0 && (
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Existing Interactions ({contact.interactions.length})</Label>
              <div className="max-h-[120px] overflow-y-auto space-y-2">
                {contact.interactions.map((interaction) => {
                  const interactionType = INTERACTION_TYPES.find(t => t.id === interaction.type);
                  const IconComponent = interactionType?.icon || Phone;
                  return (
                    <div key={interaction.id} className="text-xs bg-zinc-900/50 rounded p-2 border border-white/5 group">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 flex-1">
                          <IconComponent size={14} className="text-purple-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-zinc-400">{interaction.summary}</p>
                            {interaction.outcome && (
                              <p className="text-zinc-500 text-[10px] mt-0.5">Outcome: {interaction.outcome}</p>
                            )}
                            {interaction.nextStep && (
                              <p className="text-zinc-500 text-[10px]">Next: {interaction.nextStep}</p>
                            )}
                            <span className="text-[10px] text-zinc-600 mt-1 block">
                              {interactionType?.label} · {interaction.author} · {new Date(interaction.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => onDeleteInteraction(contact.id, interaction.id)}
                          className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-all flex-shrink-0"
                          title="Delete interaction"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add new interaction form */}
          <form onSubmit={handleSubmit} className="space-y-4 border-t border-white/5 pt-4">
            <Label className="text-zinc-300 text-xs font-medium">Log New Interaction</Label>
            
            {/* Interaction Type */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {INTERACTION_TYPES.map((type) => {
                  const IconComponent = type.icon;
                  const isSelected = formData.type === type.id;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => handleChange('type', type.id)}
                      className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border transition-all ${
                        isSelected 
                          ? 'bg-purple-500/20 border-purple-500/50 text-purple-300' 
                          : 'bg-[#111318] border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-300'
                      }`}
                    >
                      <IconComponent size={16} />
                      <span className="text-[9px] font-medium">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Summary */}
            <div className="space-y-2">
              <Label htmlFor="interaction-summary" className="text-zinc-400 text-xs">
                Summary
              </Label>
              <Textarea
                id="interaction-summary"
                value={formData.summary}
                onChange={(e) => handleChange('summary', e.target.value)}
                placeholder="What was discussed?"
                className="min-h-[60px] bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-purple-500/50 focus:ring-purple-500/20 resize-none"
              />
            </div>

            {/* Outcome & Next Step - Side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="interaction-outcome" className="text-zinc-400 text-xs">
                  Outcome
                </Label>
                <Input
                  id="interaction-outcome"
                  value={formData.outcome}
                  onChange={(e) => handleChange('outcome', e.target.value)}
                  placeholder="e.g., Positive"
                  className="bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-purple-500/50 focus:ring-purple-500/20 text-xs"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="interaction-nextstep" className="text-zinc-400 text-xs">
                  Next Step
                </Label>
                <Input
                  id="interaction-nextstep"
                  value={formData.nextStep}
                  onChange={(e) => handleChange('nextStep', e.target.value)}
                  placeholder="e.g., Follow-up call"
                  className="bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-purple-500/50 focus:ring-purple-500/20 text-xs"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="text-zinc-400 hover:text-zinc-200 hover:bg-white/5"
              >
                Close
              </Button>
              <Button
                type="submit"
                disabled={!formData.summary.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
              >
                <Plus size={16} />
                Log Interaction
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Hard Circle Pipeline Component
const HardCircle = () => {
  const toast = useToastNotification();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [fundFilter, setFundFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [draggedContact, setDraggedContact] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteContact, setNoteContact] = useState(null);
  const [isInteractionModalOpen, setIsInteractionModalOpen] = useState(false);
  const [interactionContact, setInteractionContact] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch contacts from API on mount
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pipeline/hard-circle`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setContacts(data);
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContacts();
  }, [API_URL]);

  // Get unique owners and regions for filters
  const owners = [...new Set(contacts.map(c => c.owner))];
  const regions = [...new Set(contacts.map(c => c.region))];

  // Filter contacts
  const filteredContacts = contacts.filter(contact => {
    if (ownerFilter !== 'all' && contact.owner !== ownerFilter) return false;
    if (regionFilter !== 'all' && contact.region !== regionFilter) return false;
    return true;
  });

  // Group contacts by stage
  const contactsByStage = PIPELINE_STAGES.reduce((acc, stage) => {
    acc[stage.id] = filteredContacts.filter(c => c.stage === stage.id);
    return acc;
  }, {});

  // Drag handlers
  const handleDragStart = useCallback((e, contact) => {
    setDraggedContact(contact);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', contact.id);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedContact(null);
    setDragOverColumn(null);
  }, []);

  const handleDragOver = useCallback((e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(stageId);
  }, []);

  const handleDrop = useCallback(async (e, targetStage) => {
    e.preventDefault();
    const contactId = e.dataTransfer.getData('text/plain');
    
    if (contactId) {
      // Optimistic update
      setContacts(prev => prev.map(contact => 
        contact.id === contactId 
          ? { ...contact, stage: targetStage }
          : contact
      ));
      
      // Update in backend
      try {
        await fetch(`${API_URL}/api/pipeline/hard-circle/${contactId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ stage: targetStage })
        });
        toast.success('Contact moved');
      } catch (error) {
        console.error('Error updating contact stage:', error);
        toast.error('Failed to move contact');
      }
    }
    
    setDraggedContact(null);
    setDragOverColumn(null);
  }, [API_URL, toast]);

  // Handle creating a new contact
  const handleCreateContact = useCallback(async (newContact) => {
    try {
      const response = await fetch(`${API_URL}/api/pipeline/hard-circle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newContact)
      });
      
      if (response.ok) {
        const savedContact = await response.json();
        setContacts(prev => [savedContact, ...prev]);
        toast.success('Contact added');
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error('Failed to add contact');
    }
  }, [API_URL, toast]);

  // Handle editing a contact
  const handleEditContact = useCallback((contact) => {
    setEditingContact(contact);
    setIsEditModalOpen(true);
  }, []);

  // Handle updating a contact
  const handleUpdateContact = useCallback(async (updatedContact) => {
    // Optimistic update
    setContacts(prev => prev.map(contact => 
      contact.id === updatedContact.id ? updatedContact : contact
    ));
    setEditingContact(null);
    
    // Update in backend
    try {
      await fetch(`${API_URL}/api/pipeline/hard-circle/${updatedContact.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updatedContact)
      });
      toast.success('Contact updated');
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    }
  }, [API_URL, toast]);

  // Handle deleting a contact
  const handleDeleteContact = useCallback(async (contactId) => {
    // Optimistic delete
    setContacts(prev => prev.filter(contact => contact.id !== contactId));
    
    // Delete from backend
    try {
      await fetch(`${API_URL}/api/pipeline/hard-circle/${contactId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      toast.success('Contact deleted');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  }, [API_URL, toast]);

  // Handle opening Add Note modal
  const handleOpenNoteModal = useCallback((contact) => {
    setNoteContact(contact);
    setIsNoteModalOpen(true);
  }, []);

  // Handle adding a note to a contact
  const handleAddNote = useCallback(async (contactId, note) => {
    const newTimestamp = new Date().toISOString();
    const contact = contacts.find(c => c.id === contactId);
    const updatedNotes = [...(contact?.notes || []), note];
    
    // Optimistic update
    setContacts(prev => prev.map(c => 
      c.id === contactId 
        ? { ...c, notes: updatedNotes, lastTouchTimestamp: newTimestamp }
        : c
    ));
    setNoteContact(prev => prev ? { ...prev, notes: updatedNotes, lastTouchTimestamp: newTimestamp } : null);
    
    // Update in backend
    try {
      await fetch(`${API_URL}/api/pipeline/hard-circle/${contactId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: updatedNotes, lastTouchTimestamp: newTimestamp })
      });
    } catch (error) {
      console.error('Error adding note:', error);
    }
  }, [API_URL, contacts]);

  // Handle deleting a note
  const handleDeleteNote = useCallback(async (contactId, noteId) => {
    const contact = contacts.find(c => c.id === contactId);
    const updatedNotes = (contact?.notes || []).filter(n => n.id !== noteId);
    
    // Optimistic update
    setContacts(prev => prev.map(c => 
      c.id === contactId 
        ? { ...c, notes: updatedNotes }
        : c
    ));
    setNoteContact(prev => prev ? { ...prev, notes: updatedNotes } : null);
    
    // Update in backend
    try {
      await fetch(`${API_URL}/api/pipeline/hard-circle/${contactId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: updatedNotes })
      });
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  }, [API_URL, contacts]);

  // Handle opening Log Interaction modal
  const handleOpenInteractionModal = useCallback((contact) => {
    setInteractionContact(contact);
    setIsInteractionModalOpen(true);
  }, []);

  // Handle logging an interaction
  const handleLogInteraction = useCallback(async (contactId, interaction) => {
    const newTimestamp = new Date().toISOString();
    const contact = contacts.find(c => c.id === contactId);
    const updatedInteractions = [...(contact?.interactions || []), interaction];
    
    // Optimistic update
    setContacts(prev => prev.map(c => 
      c.id === contactId 
        ? { ...c, interactions: updatedInteractions, lastTouchTimestamp: newTimestamp }
        : c
    ));
    setInteractionContact(prev => prev ? { ...prev, interactions: updatedInteractions, lastTouchTimestamp: newTimestamp } : null);
    
    // Update in backend
    try {
      await fetch(`${API_URL}/api/pipeline/hard-circle/${contactId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ interactions: updatedInteractions, lastTouchTimestamp: newTimestamp })
      });
    } catch (error) {
      console.error('Error logging interaction:', error);
    }
  }, [API_URL, contacts]);

  // Handle deleting an interaction
  const handleDeleteInteraction = useCallback(async (contactId, interactionId) => {
    const contact = contacts.find(c => c.id === contactId);
    const updatedInteractions = (contact?.interactions || []).filter(i => i.id !== interactionId);
    
    // Optimistic update
    setContacts(prev => prev.map(c => 
      c.id === contactId 
        ? { ...c, interactions: updatedInteractions }
        : c
    ));
    setInteractionContact(prev => prev ? { ...prev, interactions: updatedInteractions } : null);
    
    // Update in backend
    try {
      await fetch(`${API_URL}/api/pipeline/hard-circle/${contactId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ interactions: updatedInteractions })
      });
    } catch (error) {
      console.error('Error deleting interaction:', error);
    }
  }, [API_URL, contacts]);

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-80px)]">
        <div className="text-zinc-500">Loading contacts...</div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="flex flex-col h-[calc(100vh-48px)] lg:h-[calc(100vh-80px)] -mx-6 lg:-mx-10 -mb-6 lg:-mb-10"
    >
      {/* Page Header */}
      <div className="flex-shrink-0 bg-[#0a0a0a] border-b border-white/6 px-6 lg:px-10 py-4">
        {/* Title Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-heading font-semibold text-zinc-100">Hard Circle</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Track committed capital through legal and settlement
            </p>
          </div>
          
          {/* Add Contact Button */}
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 px-3"
          >
            <Plus size={14} />
            Add Contact
          </Button>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Owner Filter */}
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-[130px] h-8 bg-[#0f1115] border-white/10 text-zinc-300 text-xs">
              <SelectValue placeholder="Owner" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f1115] border-white/10">
              <SelectItem value="all" className="text-xs">All Owners</SelectItem>
              {owners.map(owner => (
                <SelectItem key={owner} value={owner} className="text-xs">{owner}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Fund Filter */}
          <Select value={fundFilter} onValueChange={setFundFilter}>
            <SelectTrigger className="w-[130px] h-8 bg-[#0f1115] border-white/10 text-zinc-300 text-xs">
              <SelectValue placeholder="Fund" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f1115] border-white/10">
              <SelectItem value="all" className="text-xs">All Funds</SelectItem>
              <SelectItem value="fund-1" className="text-xs">Fund I</SelectItem>
              <SelectItem value="fund-2" className="text-xs">Fund II</SelectItem>
              <SelectItem value="fund-3" className="text-xs">Fund III</SelectItem>
            </SelectContent>
          </Select>

          {/* Region Filter */}
          <Select value={regionFilter} onValueChange={setRegionFilter}>
            <SelectTrigger className="w-[130px] h-8 bg-[#0f1115] border-white/10 text-zinc-300 text-xs">
              <SelectValue placeholder="Region" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f1115] border-white/10">
              <SelectItem value="all" className="text-xs">All Regions</SelectItem>
              {regions.map(region => (
                <SelectItem key={region} value={region} className="text-xs">{region}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Kanban Board - Full remaining height and full width */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden" style={{ minHeight: 0 }}>
        <div className="h-full w-full flex divide-x divide-white/6">
          {PIPELINE_STAGES.map(stage => (
            <PipelineColumn
              key={stage.id}
              stage={stage}
              contacts={contactsByStage[stage.id] || []}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              dragOverColumn={dragOverColumn}
              onEditContact={handleEditContact}
              onDeleteContact={handleDeleteContact}
              onAddNote={handleOpenNoteModal}
              onLogInteraction={handleOpenInteractionModal}
            />
          ))}
        </div>
      </div>

      {/* Create Contact Modal */}
      <CreateContactModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onCreateContact={handleCreateContact}
        stages={PIPELINE_STAGES}
      />

      {/* Edit Contact Modal */}
      <EditContactModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        onUpdateContact={handleUpdateContact}
        contact={editingContact}
        stages={PIPELINE_STAGES}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        open={isNoteModalOpen}
        onOpenChange={setIsNoteModalOpen}
        onAddNote={handleAddNote}
        onDeleteNote={handleDeleteNote}
        contact={noteContact}
      />

      {/* Log Interaction Modal */}
      <LogInteractionModal
        open={isInteractionModalOpen}
        onOpenChange={setIsInteractionModalOpen}
        onLogInteraction={handleLogInteraction}
        onDeleteInteraction={handleDeleteInteraction}
        contact={interactionContact}
      />
    </motion.div>
  );
};

export default HardCircle;
